<template>
    <div class="partner-container">
        <div class="content">
            <div class="partner-info">
                <h3>{{ $t('order') }}</h3>
                <div class="info-section">
                    <p>{{ $t('orderDescription') }}</p>
                </div>
                <div class="currency-info">
                    <span>{{ $t('currencyType') }}:&nbsp;</span>
                    <span class="currency-type">BRL</span>
                </div>

                <!-- 金额输入 -->
                <div class="amount-section">
                    <h4>{{ $t('orderAmount') }}</h4>
                    <div class="amount-input">
                        <span class="prefix">BRL</span>
                        <el-input
                            v-model="amount"
                            :placeholder="$t('pleaseEnterOrderAmount')"
                            type="number"
                            :min="1"
                            @input="validateAmount"
                        ></el-input>
                    </div>

                    <div class="error-message" v-if="errorMessage">
                        {{ errorMessage }}
                    </div>
                </div>
            </div>
        </div>

        <!-- 确认按钮 -->
        <div class="action-bar">
            <el-button
                type="primary"
                class="confirm-btn"
                @click="handleConfirm"
                :disabled="!isValidAmount"
                :loading="upgradeLoading"
                >{{ $t('next') }}</el-button
            >
        </div>

        <!-- 支付弹窗 -->
        <qr-code-payment-dialog
            :visible.sync="paymentDialogVisible"
            :plan-name="selectedPlanName"
            :plan-amount="selectedPlanAmount"
            :plan-currency="selectedPlanCurrency"
            :qr-code-image="qrCodeImage"
            :order-sn="orderSn"
            @completed="handlePaymentCompleted"
        />
    </div>
</template>

<script>
import { createChannelOrder, getChannelToken } from '@/api/order'
import { createErrorHandler } from '@/utils/errorHandler'
import QrCodePaymentDialog from '@/components/QrCodePaymentDialog.vue'

export default {
    components: {
        QrCodePaymentDialog
    },
    data() {
        return {
            amount: '',
            errorMessage: '',
            isValidAmount: false,
            token: '',
            upgradeLoading: false,
            paymentDialogVisible: false,
            qrCodeImage: '',
            orderSn: '',
            selectedPlanName: '',
            selectedPlanAmount: '',
            selectedPlanCurrency: ''
        }
    },
    mounted() {
        this.errorHandler = createErrorHandler(this)
        this.getCreateOrderToken()
    },
    methods: {
        // 获取生成订单token
        getCreateOrderToken() {
            // 检查缓存中是否有有效的token
            const cachedData = localStorage.getItem('channelTokenCache')
            if (cachedData) {
                try {
                    const { token, expireTime } = JSON.parse(cachedData)
                    // 检查缓存是否在有效期内（2分钟）
                    if (expireTime > Date.now()) {
                        this.token = token
                        return
                    } else {
                        // 缓存已过期，删除它
                        localStorage.removeItem('channelTokenCache')
                    }
                } catch (error) {
                    console.error('解析缓存数据出错:', error)
                    localStorage.removeItem('channelTokenCache')
                }
            }
            getChannelToken()
                .then((data) => this.errorHandler.handleResponse(data))
                .then((res) => {
                    if (res.code == 200) {
                        this.token = res.data
                        // 将token存入缓存，设置2分钟过期时间
                        const cacheData = {
                            token: res.data,
                            expireTime: Date.now() + 2 * 60 * 1000 // 当前时间 + 2分钟
                        }
                        localStorage.setItem(
                            'channelTokenCache',
                            JSON.stringify(cacheData)
                        )
                        this.handleConfirm()
                    } else {
                        this.upgradeLoading = false
                    }
                })
                .catch((err) => {
                    console.log(err)
                    this.upgradeLoading = false
                })
        },
        validateAmount() {
            // 验证金额
            if (!this.amount) {
                this.errorMessage = this.$t('pleaseEnterOrderAmount')
                this.isValidAmount = false
                return
            }

            const amountNum = parseFloat(this.amount)
            if (isNaN(amountNum)) {
                this.errorMessage = this.$t('validAmount')
                this.isValidAmount = false
                return
            }

            if (amountNum <= 0) {
                this.errorMessage = this.$t('validAmount')
                this.isValidAmount = false
                return
            }

            this.errorMessage = ''
            this.isValidAmount = true
        },
        // 创建订单
        handleConfirm() {
            if (!this.isValidAmount) {
                return
            }
            this.upgradeLoading = true
            this.selectedPlanAmount = Number(this.amount).toFixed(2)
            this.selectedPlanCurrency = 'BRL'
            createChannelOrder({
                payAmount: this.amount,
                orderToken: this.token
            })
                .then((data) => this.errorHandler.handleResponse(data))
                .then((res) => {
                    if (res.code == 200) {
                        localStorage.removeItem('channelTokenCache')
                        if (res.data.paymentQrCode) {
                            // 有二维码，显示弹窗（base64图片）
                            const qrCode = res.data.paymentQrCode
                            this.qrCodeImage = qrCode.startsWith('data:image')
                                ? qrCode
                                : `data:image/png;base64,${qrCode}`
                            this.orderSn = res.data.orderSn || ''
                            this.paymentDialogVisible = true
                        } else {
                            // 无二维码，跳转支付链接
                            window.location.href = res.data.paymentLink
                        }
                    }
                    this.upgradeLoading = false
                })
                .catch((err) => {
                    console.log(err)
                    this.upgradeLoading = false
                })
        },
        // 处理支付完成
        handlePaymentCompleted() {
            this.paymentDialogVisible = false
            this.qrCodeImage = ''
            this.orderSn = ''
            this.selectedPlanName = ''
            this.selectedPlanAmount = ''
            this.selectedPlanCurrency = ''
        }
    }
}
</script>

<style lang="scss" scoped>
.partner-container {
    max-width: 800px;
    margin: 20px auto;
    padding: 20px;
    background: #fff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

    .content {
        width: 100%;
    }

    .partner-info {
        border: 1px solid #ebeef5;
        padding: 20px;
        border-radius: 4px;

        h3 {
            margin: 0 0 15px 0;
        }

        .info-section {
            margin-bottom: 15px;
        }

        .currency-info {
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px dashed #ebeef5;
            margin-bottom: 20px;

            .currency-type {
                font-weight: bold;
                color: #409eff;
            }
        }

        .amount-section {
            margin-top: 25px;
            padding-top: 20px;
            border-top: 1px dashed #ebeef5;

            h4 {
                margin: 0 0 15px 0;
            }
        }

        .amount-input {
            display: flex;
            align-items: center;
            margin-bottom: 15px;

            .prefix {
                padding: 0 20px;
                background-color: #f5f7fa;
                height: 40px;
                line-height: 40px;
                border: 1px solid #dcdfe6;
                border-right: none;
                border-radius: 4px 0 0 4px;
                color: #606266;
            }

            .el-input ::v-deep input {
                border-radius: 0 4px 4px 0;
            }
        }

        .error-message {
            color: #f56c6c;
            font-size: 12px;
            margin-bottom: 15px;
        }
    }

    .action-bar {
        margin-top: 30px;
        text-align: right;

        .confirm-btn {
            background: #f56c6c;
            border: none;
            padding: 12px 40px;

            &:disabled {
                background: #fab6b6;
            }
        }
    }
}

/* 响应式设计 */
@media (max-width: 768px) {
    .partner-container {
        margin: 10px;
        padding: 15px;

        .partner-info {
            padding: 15px;
        }

        .action-bar {
            text-align: center;

            .confirm-btn {
                width: 100%;
                padding: 10px;
            }
        }
    }
}

/* 小屏幕适配 */
@media (max-width: 480px) {
    .partner-container {
        margin: 5px;
        padding: 10px;

        h3,
        h4 {
            font-size: 16px;
        }

        .partner-info {
            padding: 10px;
        }

        .amount-input {
            flex-direction: row; /* 改为行排列 */
            align-items: center; /* 垂直居中对齐 */

            .prefix {
                padding: 0 15px; /* 添加内边距 */
                width: auto; /* 宽度自适应内容 */
                border-radius: 4px 0 0 4px; /* 恢复左侧圆角 */
                border-right: none; /* 移除右边框 */
                border-bottom: 1px solid #dcdfe6; /* 恢复底部边框 */
                text-align: center;
            }

            .el-input {
                flex: 1; /* 让输入框占据剩余空间 */
            }

            .el-input :deep(input) {
                border-radius: 0 4px 4px 0; /* 恢复右侧圆角 */
            }
        }
    }
}
</style>